/**
 * @fileOverview Nano Siege - the endless idle tower-defence game.
 *
 * Everything in this file is pure: no React, no Firestore, no clock reads. The
 * client passes a base and a timestamp in, gets a new base out. That is what
 * makes the whole economy testable, and it is what lets the same code run the
 * live board at 60fps AND settle eight hours of absence in one call.
 *
 * THE SHAPE OF THE GAME
 *
 * Waves of orbs walk a fixed track. Towers standing beside it shoot them. An
 * orb that survives the track takes a bite out of your integrity; run out and
 * the wave restarts rather than wiping you, because an idle game you can lose
 * overnight is an idle game nobody leaves running.
 *
 * Popping an orb does not remove it so much as peel it: every tier above the
 * smallest breaks into children of the tier below, so one Titan is really 185
 * points of health arriving in stages. That is what makes splash and slow worth
 * buying instead of stacking one damage number forever.
 *
 * THE WALL, AND WHY IT IS DELIBERATE
 *
 * Wave health compounds faster than a tower level does (see WAVE_HP_GROWTH vs
 * TOWER_DAMAGE_GROWTH). Left alone, every base eventually meets a wave it
 * cannot clear. That stall is the game: it is what makes Overclocking - trading
 * the whole base for permanent sigils - feel like relief instead of a penalty.
 * Do not "fix" it by raising damage growth above health growth; that flattens
 * the curve into a straight line and the game ends.
 *
 * ON TRUST
 *
 * Flux, sigils and wave numbers are computed and written by the client, exactly
 * like the Night Market stall. That is fine because they are sealed: they buy
 * nothing outside this game and are never read for another player. Perks are
 * the one exception - they are bought with real site credits and nano cores, so
 * the SPEND goes through the same client-side users-doc write the shop already
 * uses. Anyone who forges a perk could equally have forged the currency, so
 * this adds no new hole; but if the shop ever moves to a Cloud Function, the
 * perk purchase has to move with it.
 */

// --- Track -------------------------------------------------------------------

export interface SiegePoint {
  x: number;
  y: number;
}

interface SiegeSegment {
  from: SiegePoint;
  to: SiegePoint;
  length: number;
  start: number;
}

/**
 * One battlefield: a track to walk and the pads a tower may stand on.
 *
 * Every map is drawn in the same 0-100 x / 0-60 y space, and every track starts
 * and ends off-screen so orbs walk in and out of frame rather than popping into
 * existence at the edge. Later maps are longer and carry more pads, which is
 * most of why a run keeps growing rather than flattening out.
 */
export interface SiegeMap {
  id: string;
  name: string;
  /** One-line description of what makes this board different to play. */
  character: string;
  path: SiegePoint[];
  slots: SiegePoint[];
  /** Total track length in board units. Orb speeds are in the same units. */
  length: number;
  segments: SiegeSegment[];
}

function buildMap(id: string, name: string, character: string, path: SiegePoint[], slots: SiegePoint[]): SiegeMap {
  const segments: SiegeSegment[] = [];
  let total = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const from = path[i];
    const to = path[i + 1];
    const length = Math.hypot(to.x - from.x, to.y - from.y);
    segments.push({ from, to, length, start: total });
    total += length;
  }
  return { id, name, character, path, slots, length: total, segments };
}

/**
 * The map rotation.
 *
 * A run walks through these in order, one per sector, and wraps when it runs
 * out. Each has a different shape rather than a different skin: a long straight
 * rewards pierce, a tight coil rewards range, and a board whose pads all sit on
 * the outside punishes short-range towers.
 */
export const SIEGE_MAPS: SiegeMap[] = [
  buildMap(
    'serpentine',
    'Serpentine',
    'Four long straights and tight corners. Forgiving, and the board everyone learns on.',
    [
      { x: -4, y: 12 }, { x: 26, y: 12 }, { x: 26, y: 30 }, { x: 8, y: 30 }, { x: 8, y: 48 },
      { x: 56, y: 48 }, { x: 56, y: 22 }, { x: 78, y: 22 }, { x: 78, y: 52 }, { x: 104, y: 52 },
    ],
    [
      { x: 12, y: 20 }, { x: 40, y: 20 }, { x: 66, y: 12 }, { x: 90, y: 16 },
      { x: 20, y: 40 }, { x: 40, y: 36 }, { x: 66, y: 34 }, { x: 90, y: 30 },
      { x: 14, y: 56 }, { x: 34, y: 56 }, { x: 68, y: 58 }, { x: 90, y: 44 },
    ],
  ),
  buildMap(
    'coil',
    'The Coil',
    'A track that spirals in on itself. A single well-placed long-range tower can cover three passes at once.',
    [
      { x: -4, y: 6 }, { x: 92, y: 6 }, { x: 92, y: 54 }, { x: 10, y: 54 }, { x: 10, y: 18 },
      { x: 78, y: 18 }, { x: 78, y: 42 }, { x: 24, y: 42 }, { x: 24, y: 30 }, { x: 64, y: 30 },
      { x: 64, y: 66 },
    ],
    [
      { x: 4, y: 12 }, { x: 30, y: 12 }, { x: 56, y: 12 }, { x: 84, y: 12 },
      { x: 17, y: 24 }, { x: 44, y: 24 }, { x: 71, y: 24 }, { x: 96, y: 30 },
      { x: 44, y: 36 }, { x: 17, y: 48 }, { x: 44, y: 48 }, { x: 71, y: 48 },
      { x: 88, y: 48 }, { x: 4, y: 36 },
    ],
  ),
  buildMap(
    'gauntlet',
    'The Gauntlet',
    'Five long parallel runs. Made for Ion Lances firing straight down a column.',
    [
      { x: -4, y: 8 }, { x: 96, y: 8 }, { x: 96, y: 20 }, { x: 4, y: 20 }, { x: 4, y: 32 },
      { x: 96, y: 32 }, { x: 96, y: 44 }, { x: 4, y: 44 }, { x: 4, y: 56 }, { x: 104, y: 56 },
    ],
    [
      { x: 14, y: 14 }, { x: 36, y: 14 }, { x: 58, y: 14 }, { x: 80, y: 14 },
      { x: 14, y: 26 }, { x: 36, y: 26 }, { x: 58, y: 26 }, { x: 80, y: 26 },
      { x: 14, y: 38 }, { x: 36, y: 38 }, { x: 58, y: 38 }, { x: 80, y: 38 },
      { x: 25, y: 50 }, { x: 69, y: 50 },
    ],
  ),
  buildMap(
    'crucible',
    'The Crucible',
    'A wide circuit with every pad pushed to the outside. Short-range towers starve here.',
    [
      { x: 50, y: -4 }, { x: 50, y: 14 }, { x: 20, y: 14 }, { x: 20, y: 46 }, { x: 80, y: 46 },
      { x: 80, y: 14 }, { x: 62, y: 14 }, { x: 62, y: 30 }, { x: 38, y: 30 }, { x: 38, y: 64 },
    ],
    [
      { x: 6, y: 8 }, { x: 34, y: 6 }, { x: 66, y: 6 }, { x: 94, y: 8 },
      { x: 6, y: 30 }, { x: 50, y: 22 }, { x: 94, y: 30 },
      { x: 6, y: 52 }, { x: 50, y: 38 }, { x: 94, y: 52 },
      { x: 26, y: 58 }, { x: 72, y: 58 }, { x: 50, y: 52 },
    ],
  ),
];

export const SIEGE_MAPS_BY_ID = new Map(SIEGE_MAPS.map((map) => [map.id, map]));

/**
 * The most pads any map has. `base.towers` is always this long so that the save
 * shape does not change when the map does; indices past the current map's pad
 * count are simply not addressable.
 */
export const MAX_SIEGE_SLOTS = SIEGE_MAPS.reduce((most, map) => Math.max(most, map.slots.length), 0);

/** Kept as the default board for anything that just needs a shape to draw. */
export const DEFAULT_SIEGE_MAP = SIEGE_MAPS[0];

export function getSiegeMap(id: string | undefined): SiegeMap {
  return (id && SIEGE_MAPS_BY_ID.get(id)) || DEFAULT_SIEGE_MAP;
}

/** Where an orb that has walked `distance` units along `map` is standing. */
export function pointOnMap(map: SiegeMap, distance: number): SiegePoint {
  if (!(distance > 0)) return map.path[0];
  if (distance >= map.length) return map.path[map.path.length - 1];
  for (const segment of map.segments) {
    if (distance <= segment.start + segment.length) {
      const t = segment.length === 0 ? 0 : (distance - segment.start) / segment.length;
      return {
        x: segment.from.x + (segment.to.x - segment.from.x) * t,
        y: segment.from.y + (segment.to.y - segment.from.y) * t,
      };
    }
  }
  return map.path[map.path.length - 1];
}

// --- Orbs --------------------------------------------------------------------

export type OrbTierId = 'spark' | 'flux' | 'prism' | 'void' | 'nova' | 'titan';

export interface OrbTier {
  id: OrbTierId;
  name: string;
  /** Health of this shell alone, before its children are counted. */
  shellHp: number;
  /** Board units per second. */
  speed: number;
  /** What this shell breaks into when it pops. */
  child: OrbTierId | null;
  childCount: number;
  /** Integrity lost if this shell alone reaches the end. */
  leak: number;
  color: string;
  glow: string;
}

export const ORB_TIERS: Record<OrbTierId, OrbTier> = {
  spark: { id: 'spark', name: 'Spark', shellHp: 1, speed: 16, child: null, childCount: 0, leak: 1, color: '#4ade80', glow: 'rgba(74,222,128,0.55)' },
  flux: { id: 'flux', name: 'Flux', shellHp: 2, speed: 14, child: 'spark', childCount: 1, leak: 1, color: '#38bdf8', glow: 'rgba(56,189,248,0.55)' },
  prism: { id: 'prism', name: 'Prism', shellHp: 3, speed: 12, child: 'flux', childCount: 2, leak: 2, color: '#c084fc', glow: 'rgba(192,132,252,0.55)' },
  void: { id: 'void', name: 'Void', shellHp: 5, speed: 10.5, child: 'prism', childCount: 2, leak: 3, color: '#fb7185', glow: 'rgba(251,113,133,0.55)' },
  nova: { id: 'nova', name: 'Nova', shellHp: 9, speed: 9, child: 'void', childCount: 2, leak: 5, color: '#fbbf24', glow: 'rgba(251,191,36,0.6)' },
  titan: { id: 'titan', name: 'Titan', shellHp: 20, speed: 6.5, child: 'nova', childCount: 3, leak: 12, color: '#f8fafc', glow: 'rgba(248,250,252,0.7)' },
};

/** Tiers weakest first. The wave builder walks this backwards. */
export const ORB_TIER_ORDER: OrbTierId[] = ['spark', 'flux', 'prism', 'void', 'nova', 'titan'];

/** Health of a shell plus everything hiding inside it. */
export const getOrbTotalHp = (() => {
  const cache = new Map<OrbTierId, number>();
  return function totalHp(tierId: OrbTierId): number {
    const cached = cache.get(tierId);
    if (cached !== undefined) return cached;
    const tier = ORB_TIERS[tierId];
    const value = tier.shellHp + (tier.child ? tier.childCount * totalHp(tier.child) : 0);
    cache.set(tierId, value);
    return value;
  };
})();

/** Integrity a shell costs if it and all its children leak. */
export const getOrbTotalLeak = (() => {
  const cache = new Map<OrbTierId, number>();
  return function totalLeak(tierId: OrbTierId): number {
    const cached = cache.get(tierId);
    if (cached !== undefined) return cached;
    const tier = ORB_TIERS[tierId];
    const value = tier.leak + (tier.child ? tier.childCount * totalLeak(tier.child) : 0);
    cache.set(tierId, value);
    return value;
  };
})();

// --- Wave curve --------------------------------------------------------------

/** Effective health of wave 1. Two seconds of one bare Pulse Emitter. */
export const WAVE_BASE_HP = 4;

/**
 * How fast a wave's health compounds.
 *
 * This number is not free. What the player feels is the RATIO between wave
 * health and the damage their flux can buy, and that damage works out at about
 * WAVE_REWARD_GROWTH ^ (ln TOWER_DAMAGE_GROWTH / ln TOWER_COST_GROWTH) per
 * wave. With the values below that leaves each wave roughly 5.5% slower than
 * the one before it.
 *
 * That number is the whole difficulty curve, and it has been wrong twice in
 * both directions: 1.14 here with 1.155 rewards reached wave 292 in one hour
 * (nothing meant anything), and 1.19 walled a first run at wave 40 in eleven
 * minutes (over before it started). At 1.128 a first run passes its first
 * sector in a few minutes, reaches wave 50 in under half an hour and wave 100
 * over a long session, which is the room the sector rotation needs to breathe.
 */
export const WAVE_HP_GROWTH = 1.128;

/** Every tenth wave is a boss: fewer, much heavier shells. */
export const BOSS_WAVE_INTERVAL = 10;
export const BOSS_HP_MULTIPLIER = 2.6;

/**
 * Ceiling on how many shells one wave may spawn. Past this the wave's health is
 * delivered by making each shell heavier instead of adding more of them, which
 * is the only reason wave 900 costs the same to simulate as wave 9.
 */
export const MAX_WAVE_SHELLS = 48;

/**
 * The wave a tier first shows up on.
 *
 * Deliberately front-loaded: a first run walls out somewhere around wave 40, so
 * anything unlocking later than the low thirties would never be seen by a
 * player who has not prestiged at least once.
 */
const TIER_UNLOCK_WAVE: Record<OrbTierId, number> = {
  spark: 1,
  flux: 4,
  prism: 8,
  void: 14,
  nova: 22,
  titan: 32,
};

export function isBossWave(wave: number): boolean {
  const w = Math.max(1, Math.floor(wave));
  return w % BOSS_WAVE_INTERVAL === 0;
}

// --- Sectors -----------------------------------------------------------------

/**
 * Waves per sector.
 *
 * A multiple of BOSS_WAVE_INTERVAL on purpose, so a sector always ends on a
 * boss: the last wave of a sector is a finale rather than an arbitrary number.
 */
export const SECTOR_LENGTH = 20;

/** How much stronger clearing a sector leaves you for the rest of the run. */
export const SECTOR_DAMAGE_BONUS = 0.2;
export const SECTOR_FLUX_BONUS = 0.2;

/** 1-based. Wave 1-20 is sector 1, 21-40 is sector 2, and so on forever. */
export function getSector(wave: number): number {
  return Math.floor((Math.max(1, Math.floor(wave)) - 1) / SECTOR_LENGTH) + 1;
}

/** True on the boss wave that ends a sector. */
export function isSectorFinalWave(wave: number): boolean {
  return Math.max(1, Math.floor(wave)) % SECTOR_LENGTH === 0;
}

/** The board a given wave is fought on. Sectors walk the rotation and wrap. */
export function getMapForWave(wave: number): SiegeMap {
  return SIEGE_MAPS[(getSector(wave) - 1) % SIEGE_MAPS.length];
}

/** Sectors finished this run. Resets with the run, like the wave number does. */
export function getSectorsCleared(base: SiegeBase): number {
  return getSector(base.wave) - 1;
}

/** Total effective health of `wave`, children included. */
export function getWaveHp(wave: number): number {
  const w = Math.max(1, Math.floor(wave));
  const base = WAVE_BASE_HP * Math.pow(WAVE_HP_GROWTH, w - 1);
  return base * (isBossWave(w) ? BOSS_HP_MULTIPLIER : 1);
}

// --- Wave modifiers ----------------------------------------------------------

/**
 * A trait carried by every shell in a modified wave.
 *
 * Traits exist to make the board you built matter more than the number it adds
 * up to. Each one is beaten by a different tower, so a base that is only one
 * kind of gun has a wave it is bad at no matter how many levels it has bought.
 */
export type OrbTrait = 'armored' | 'shielded' | 'swift' | 'regen';

export type WaveModifierId = 'none' | 'armored' | 'shielded' | 'swift' | 'regen' | 'swarm' | 'boss';

export interface WaveModifier {
  id: WaveModifierId;
  name: string;
  /** What it does, in the player's terms. */
  description: string;
  /** The tower that answers it. Shown in the build phase as a hint. */
  counter: string;
  trait: OrbTrait | null;
}

/** Flat damage soaked from every hit, as a share of the shell's own health. */
export const ARMOR_SHARE = 0.55;
/** No hit is ever fully absorbed; armour caps at this much of a hit. */
export const ARMOR_MAX_REDUCTION = 0.9;
/** How much faster a swift wave walks. */
export const SWIFT_SPEED = 1.7;
/**
 * Share of max health a regenerating shell heals per second once left alone.
 *
 * Kept low deliberately. Regeneration is the one modifier that sets a hard
 * FLOOR on damage rather than just costing time: below it a wave literally
 * cannot be finished. At 6% this produced a staircase of impassable walls
 * fifteen waves apart, each needing six times the damage of the last, so a
 * player who tripled their damage got nowhere at all. At 2.5% it is a real
 * threat that more damage always eventually answers.
 */
export const REGEN_PER_SECOND = 0.025;
/** Seconds without damage before regeneration starts. */
export const REGEN_DELAY_SECONDS = 1.5;
/** A swarm wave trades shell health for shell count. */
export const SWARM_COUNT_MULTIPLIER = 2;

export const WAVE_MODIFIERS: Record<WaveModifierId, WaveModifier> = {
  none: { id: 'none', name: 'Standard', description: 'Nothing unusual on the scanner.', counter: '', trait: null },
  armored: {
    id: 'armored',
    name: 'Armored',
    description: 'Every shell soaks a flat amount from each hit, so lots of tiny hits barely scratch it.',
    counter: 'Railgun and Ion Lance - few, heavy hits',
    trait: 'armored',
  },
  shielded: {
    id: 'shielded',
    name: 'Shielded',
    description: 'Splash damage washes straight off. Only what a tower hits directly counts.',
    counter: 'Pulse Emitter and Railgun - direct fire',
    trait: 'shielded',
  },
  swift: {
    id: 'swift',
    name: 'Swift',
    description: 'The column moves far faster, so every tower gets fewer shots before it is past.',
    counter: 'Frost Node - slow them back down',
    trait: 'swift',
  },
  regen: {
    id: 'regen',
    name: 'Regenerating',
    description: 'Shells heal themselves the moment nothing is shooting them.',
    counter: 'Wide coverage - never let them out of range',
    trait: 'regen',
  },
  swarm: {
    id: 'swarm',
    name: 'Swarm',
    description: 'Twice as many shells, each half as tough. Single-target guns will drown.',
    counter: 'Scatter Coil and Ion Lance - hit many at once',
    trait: null,
  },
  boss: {
    id: 'boss',
    name: 'Boss',
    description: 'One heavily reinforced assault. Far more health, and triple the flux for clearing it.',
    counter: 'Everything you have - and save your Overdrive',
    // Deliberately NO trait. A boss already carries BOSS_HP_MULTIPLIER; adding
    // armour on top made wave 10 an impossible wall for a first-run board,
    // which is a stop, not a difficulty spike.
    trait: null,
  },
};

/** Modifiers enter the rotation once the basics have been taught. */
export const MODIFIER_START_WAVE = 12;
/** One wave in this many carries a modifier, outside of boss waves. */
export const MODIFIER_INTERVAL = 3;

const MODIFIER_ROTATION: WaveModifierId[] = ['armored', 'swarm', 'swift', 'shielded', 'regen'];

/**
 * Which modifier `wave` carries.
 *
 * Deterministic, so the build phase can show it before you commit and two
 * players on the same wave are looking at the same problem.
 */
export function getWaveModifier(wave: number): WaveModifier {
  const w = Math.max(1, Math.floor(wave));
  if (isBossWave(w)) return WAVE_MODIFIERS.boss;
  if (w < MODIFIER_START_WAVE || w % MODIFIER_INTERVAL !== 0) return WAVE_MODIFIERS.none;
  const index = Math.floor(w / MODIFIER_INTERVAL) % MODIFIER_ROTATION.length;
  return WAVE_MODIFIERS[MODIFIER_ROTATION[index]];
}

export interface WaveShell {
  tier: OrbTierId;
  /** Multiplier applied to every shell health in this wave. Always >= 1. */
  hpScale: number;
  traits: OrbTrait[];
}

export interface WavePlan {
  wave: number;
  shells: WaveShell[];
  /** The health the shells actually add up to. Matches getWaveHp closely. */
  totalHp: number;
  hpScale: number;
  isBoss: boolean;
  modifier: WaveModifier;
  /** Seconds between one shell entering the track and the next. */
  spawnIntervalSeconds: number;
  /** Shell counts by tier, for the build-phase readout. */
  composition: { tier: OrbTierId; count: number }[];
}

/**
 * Build the wave.
 *
 * Greedy from the heaviest unlocked tier down, so a wave reads as a few big
 * shells with a tail of small ones rather than a uniform column - and the tail
 * is what keeps early towers relevant at high waves.
 */
export function getWavePlan(wave: number): WavePlan {
  const w = Math.max(1, Math.floor(wave));
  const target = getWaveHp(w);
  const unlocked = ORB_TIER_ORDER.filter((tier) => TIER_UNLOCK_WAVE[tier] <= w);
  const shells: OrbTierId[] = [];

  let remaining = target;
  for (let i = unlocked.length - 1; i >= 0; i--) {
    const tier = unlocked[i];
    const hp = getOrbTotalHp(tier);
    // Leave the lightest tier to mop up whatever is left, and never let a
    // single tier eat the whole wave - a wave of one shell is not a wave.
    const isLightest = i === 0;
    const budget = isLightest ? remaining : remaining * 0.68;
    let count = Math.floor(budget / hp);
    if (count <= 0) continue;
    if (shells.length + count > MAX_WAVE_SHELLS) count = MAX_WAVE_SHELLS - shells.length;
    for (let n = 0; n < count; n++) shells.push(tier);
    remaining -= count * hp;
    if (shells.length >= MAX_WAVE_SHELLS) break;
  }

  if (shells.length === 0) shells.push(unlocked[0] || 'spark');

  const modifier = getWaveModifier(w);

  // A swarm splits the same health across twice as many shells rather than
  // adding health, so it is a shape change, not a difficulty spike.
  if (modifier.id === 'swarm') {
    const doubled: OrbTierId[] = [];
    for (const tier of shells) {
      for (let n = 0; n < SWARM_COUNT_MULTIPLIER; n++) doubled.push(tier);
      if (doubled.length >= MAX_WAVE_SHELLS) break;
    }
    shells.length = 0;
    shells.push(...doubled.slice(0, MAX_WAVE_SHELLS));
  }

  const rawHp = shells.reduce((sum, tier) => sum + getOrbTotalHp(tier), 0);
  // Normally a shell is never weaker than its own tier, which keeps early waves
  // honest. A swarm is the deliberate exception: it carries the SAME health as
  // any other wave, spread across twice the shells, so each one has to be
  // allowed to drop below its tier or the wave would simply be double strength.
  const minScale = modifier.id === 'swarm' ? 0 : 1;
  const hpScale = rawHp > 0 ? Math.max(minScale, target / rawHp) : 1;
  const traits: OrbTrait[] = modifier.trait ? [modifier.trait] : [];

  const composition: { tier: OrbTierId; count: number }[] = [];
  for (const tier of ORB_TIER_ORDER) {
    const count = shells.filter((shell) => shell === tier).length;
    if (count > 0) composition.unshift({ tier, count });
  }

  return {
    wave: w,
    shells: shells.map((tier) => ({ tier, hpScale, traits })),
    totalHp: rawHp * hpScale,
    hpScale,
    isBoss: isBossWave(w),
    modifier,
    // Long waves are boring and short ones are unreadable; this keeps the
    // spawn column between roughly four and twelve seconds at any wave size.
    spawnIntervalSeconds: Math.max(0.18, Math.min(0.85, 9 / Math.max(1, shells.length))),
    composition,
  };
}

/**
 * Flux paid for clearing `wave`. Grows more slowly than upgrade costs do, which
 * is what stops a base from out-buying the wave curve forever. See the note on
 * WAVE_HP_GROWTH before touching it.
 */
export const WAVE_REWARD_BASE = 12;
export const WAVE_REWARD_GROWTH = 1.09;

export function getWaveReward(wave: number): number {
  const w = Math.max(1, Math.floor(wave));
  const reward = WAVE_REWARD_BASE * Math.pow(WAVE_REWARD_GROWTH, w - 1);
  return reward * (isBossWave(w) ? 3 : 1);
}

// --- Towers ------------------------------------------------------------------

export type TowerId = 'pulse' | 'scatter' | 'frost' | 'lance' | 'railgun' | 'harvest';

export interface TowerDef {
  id: TowerId;
  name: string;
  role: string;
  description: string;
  /** Cost of putting one on an empty slot. */
  baseCost: number;
  /** Damage per shot at level 1. */
  damage: number;
  /** Shots per second at level 1. */
  fireRate: number;
  /** Board units. */
  range: number;
  /** Radius of collateral damage around the hit, in board units. 0 = none. */
  splash: number;
  /** Fraction of full splash damage the neighbours take. */
  splashFalloff: number;
  /** How much this tower slows what it hits (0-1), and for how long. */
  slow: number;
  slowSeconds: number;
  /** Extra shells one shot passes through. */
  pierce: number;
  /** Flux per second generated regardless of combat. */
  fluxPerSecond: number;
  color: string;
}

export const TOWER_DEFS: Record<TowerId, TowerDef> = {
  pulse: {
    id: 'pulse',
    name: 'Pulse Emitter',
    role: 'Backbone',
    description: 'Cheap, fast, always shooting. Never the best tower on the board and never a wasted one.',
    baseCost: 25,
    damage: 1,
    fireRate: 2.2,
    range: 20,
    splash: 0,
    splashFalloff: 0,
    slow: 0,
    slowSeconds: 0,
    pierce: 0,
    fluxPerSecond: 0,
    color: '#22d3ee',
  },
  scatter: {
    id: 'scatter',
    name: 'Scatter Coil',
    role: 'Crowd',
    description: 'Bursts on impact. The answer to a shell that breaks into six of something smaller.',
    baseCost: 90,
    damage: 1.6,
    fireRate: 1.1,
    range: 17,
    splash: 9,
    splashFalloff: 0.6,
    slow: 0,
    slowSeconds: 0,
    pierce: 0,
    fluxPerSecond: 0,
    color: '#fb923c',
  },
  frost: {
    id: 'frost',
    name: 'Frost Node',
    role: 'Control',
    description: 'Barely scratches anything, but a slowed orb spends longer inside every other tower range.',
    baseCost: 140,
    damage: 0.4,
    fireRate: 1.4,
    range: 22,
    splash: 11,
    splashFalloff: 1,
    slow: 0.42,
    slowSeconds: 2.4,
    pierce: 0,
    fluxPerSecond: 0,
    color: '#7dd3fc',
  },
  lance: {
    id: 'lance',
    name: 'Ion Lance',
    role: 'Pierce',
    description: 'One beam, straight through the column. Loves a long queue of shells on a straight.',
    baseCost: 320,
    damage: 6,
    fireRate: 0.75,
    range: 26,
    splash: 0,
    splashFalloff: 0,
    slow: 0,
    slowSeconds: 0,
    pierce: 3,
    fluxPerSecond: 0,
    color: '#a78bfa',
  },
  railgun: {
    id: 'railgun',
    name: 'Railgun',
    role: 'Single target',
    description: 'Slow, expensive, and the only thing that meaningfully hurts a Titan.',
    baseCost: 900,
    damage: 34,
    fireRate: 0.34,
    range: 40,
    splash: 5,
    splashFalloff: 0.35,
    slow: 0,
    slowSeconds: 0,
    pierce: 0,
    fluxPerSecond: 0,
    color: '#f472b6',
  },
  harvest: {
    id: 'harvest',
    name: 'Flux Harvester',
    role: 'Economy',
    description: 'Shoots nothing. Prints flux every second, including the seconds you are not here.',
    baseCost: 200,
    damage: 0,
    fireRate: 0,
    range: 0,
    splash: 0,
    splashFalloff: 0,
    slow: 0,
    slowSeconds: 0,
    pierce: 0,
    fluxPerSecond: 1.6,
    color: '#4ade80',
  },
};

export const TOWER_ORDER: TowerId[] = ['pulse', 'scatter', 'frost', 'lance', 'railgun', 'harvest'];

/** What one more level is worth. Below WAVE_HP_GROWTH on purpose. */
export const TOWER_DAMAGE_GROWTH = 1.115;
/** What one more level costs. */
export const TOWER_COST_GROWTH = 1.15;
export const TOWER_MAX_LEVEL = 500;

/**
 * Fire rate improves too, but much more slowly than damage - otherwise a level
 * 200 Railgun turns into a machine gun and the tower roles collapse into one.
 */
export const TOWER_FIRE_RATE_GROWTH = 1.014;
export const TOWER_FIRE_RATE_CAP = 6;

/** Cost of taking a tower from `level` to `level + 1`. Level 0 = buying it. */
export function getTowerUpgradeCost(towerId: TowerId, level: number): number {
  const def = TOWER_DEFS[towerId];
  if (!def) return Number.POSITIVE_INFINITY;
  const current = Math.max(0, Math.floor(level || 0));
  if (current >= TOWER_MAX_LEVEL) return Number.POSITIVE_INFINITY;
  if (current === 0) return def.baseCost;
  return Math.ceil(def.baseCost * 0.55 * Math.pow(TOWER_COST_GROWTH, current));
}

// --- Perks -------------------------------------------------------------------

export type SiegePerkId =
  | 'calibration'
  | 'capacitor'
  | 'plating'
  | 'reserve'
  | 'overdrive'
  | 'autodeploy'
  | 'deepcore'
  | 'fabricator'
  | 'singularity'
  | 'timewarp'
  | 'prime';

export interface SiegePerk {
  id: SiegePerkId;
  name: string;
  description: string;
  price: number;
  currency: 'credits' | 'nanoCores';
}

/**
 * Permanent, one-time, account-wide. Survives Overclock - that is the whole
 * point of paying for them.
 *
 * Credit perks are the ones you grind for; nano-core perks are the ones that
 * change how the game is played while you are not looking at it. Nothing here
 * is required to reach any wave, only to reach it sooner.
 */
export const SIEGE_PERKS: SiegePerk[] = [
  { id: 'calibration', name: 'Field Calibration', description: 'Every tower deals 15% more damage, forever.', price: 250, currency: 'credits' },
  { id: 'capacitor', name: 'Capacitor Bank', description: 'All flux income up 25%, including wave rewards and harvesters.', price: 500, currency: 'credits' },
  { id: 'plating', name: 'Reactive Plating', description: 'Base integrity up 50%, so a bad wave takes far longer to break you.', price: 750, currency: 'credits' },
  { id: 'reserve', name: 'Strategic Reserve', description: 'Start every Overclock with 750 flux instead of nothing.', price: 1000, currency: 'credits' },
  { id: 'overdrive', name: 'Overdrive Coils', description: 'Every tower fires 12% faster.', price: 1500, currency: 'credits' },
  { id: 'autodeploy', name: 'Auto-Deploy Core', description: 'The base buys and upgrades its own towers while you are away, and never waits for you to start a wave.', price: 10, currency: 'nanoCores' },
  { id: 'deepcore', name: 'Deep Core Battery', description: 'Offline progress runs for 24 hours instead of 12.', price: 15, currency: 'nanoCores' },
  { id: 'fabricator', name: 'Auto-Fabricator', description: 'Offline progress pays 85% of live rate instead of 50%.', price: 20, currency: 'nanoCores' },
  { id: 'singularity', name: 'Singularity Lens', description: 'Doubles the damage of every tower on the board.', price: 30, currency: 'nanoCores' },
  { id: 'timewarp', name: 'Temporal Governor', description: 'The whole siege runs at double speed, online and off.', price: 45, currency: 'nanoCores' },
  { id: 'prime', name: 'Prime Directive', description: 'Overclock yields 30% more sigils, and never fewer than one.', price: 60, currency: 'nanoCores' },
];

export const SIEGE_PERKS_BY_ID = new Map(SIEGE_PERKS.map((perk) => [perk.id, perk]));

export interface SiegePerkEffects {
  damageMultiplier: number;
  fireRateMultiplier: number;
  fluxMultiplier: number;
  integrityMultiplier: number;
  /** Flux handed to a freshly overclocked base. */
  startingFlux: number;
  offlineCapMillis: number;
  offlineEfficiency: number;
  sigilMultiplier: number;
  /** Overclock never pays less than this once Prime Directive is owned. */
  minimumSigils: number;
  autoDeploy: boolean;
  speedMultiplier: number;
}

export const OFFLINE_CAP_MILLIS = 12 * 60 * 60 * 1000;
export const OFFLINE_EFFICIENCY = 0.5;

export function getPerkEffects(perks: SiegePerkId[] | undefined): SiegePerkEffects {
  const owned = new Set(perks || []);
  return {
    damageMultiplier: (owned.has('calibration') ? 1.15 : 1) * (owned.has('singularity') ? 2 : 1),
    fireRateMultiplier: owned.has('overdrive') ? 1.12 : 1,
    fluxMultiplier: owned.has('capacitor') ? 1.25 : 1,
    integrityMultiplier: owned.has('plating') ? 1.5 : 1,
    startingFlux: owned.has('reserve') ? 750 : 0,
    offlineCapMillis: owned.has('deepcore') ? 24 * 60 * 60 * 1000 : OFFLINE_CAP_MILLIS,
    offlineEfficiency: owned.has('fabricator') ? 0.85 : OFFLINE_EFFICIENCY,
    sigilMultiplier: owned.has('prime') ? 1.3 : 1,
    minimumSigils: owned.has('prime') ? 1 : 0,
    autoDeploy: owned.has('autodeploy'),
    speedMultiplier: owned.has('timewarp') ? 2 : 1,
  };
}

// --- Sigils (prestige) -------------------------------------------------------

/** What one sigil is worth. Both compound, which is why a reset ever pays. */
export const SIGIL_DAMAGE_BONUS = 0.09;
export const SIGIL_FLUX_BONUS = 0.07;

/** The first Overclock unlocks at this wave. */
export const OVERCLOCK_MIN_WAVE = 25;

/**
 * The payout is exponential in the wave reached, not polynomial.
 *
 * Wave health is exponential, so a polynomial payout is worth less every single
 * run - resetting would be a strictly worse move than grinding, and nobody
 * would ever press the button. Growing at roughly half the rate of the wave
 * curve means each Overclock buys back a meaningful slice of the wall (the
 * first one is worth about +4 waves, a later one tens) without ever quite
 * catching it, which is what keeps the loop turning.
 */
export const SIGIL_BASE_YIELD = 6;
export const SIGIL_GROWTH = 1.045;

/** Sigils a base would be paid for Overclocking right now. */
export function getOverclockSigils(base: SiegeBase): number {
  const effects = getPerkEffects(base.perks);
  const best = Math.max(0, Math.floor(base.runBestWave || 0));
  if (best < OVERCLOCK_MIN_WAVE) return 0;
  const raw = SIGIL_BASE_YIELD * Math.pow(SIGIL_GROWTH, best - OVERCLOCK_MIN_WAVE) * effects.sigilMultiplier;
  return Math.max(effects.minimumSigils, Math.floor(raw));
}

export function canOverclock(base: SiegeBase): boolean {
  return getOverclockSigils(base) > 0;
}

/** How many more waves this run needs before Overclock pays one more sigil. */
export function wavesToNextSigil(base: SiegeBase): number {
  const best = Math.max(0, Math.floor(base.runBestWave || 0));
  if (best < OVERCLOCK_MIN_WAVE) return OVERCLOCK_MIN_WAVE - best;
  const effects = getPerkEffects(base.perks);
  const target = getOverclockSigils(base) + 1;
  const exponent = Math.log(target / (SIGIL_BASE_YIELD * effects.sigilMultiplier)) / Math.log(SIGIL_GROWTH);
  const needed = OVERCLOCK_MIN_WAVE + exponent;
  return Math.max(1, Math.ceil(needed) - best);
}

// --- Base state --------------------------------------------------------------

/**
 * Which orb a tower shoots when several are in range.
 *
 * The single cheapest way to make position matter: the same Railgun is a
 * leak-stopper on `last` and a boss-killer on `strongest`, and choosing wrong
 * is a real mistake that costs no flux to fix.
 */
export type TargetingMode = 'first' | 'last' | 'strongest' | 'closest';

export const TARGETING_MODES: { id: TargetingMode; name: string; description: string }[] = [
  { id: 'first', name: 'First', description: 'The orb nearest your core. The safe default.' },
  { id: 'last', name: 'Last', description: 'The orb furthest from your core. Good for softening a column early.' },
  { id: 'strongest', name: 'Strongest', description: 'Whatever has the most health left. Point your heavy guns here.' },
  { id: 'closest', name: 'Closest', description: 'Whatever is nearest this tower. Keeps a corner tower busy.' },
];

export interface SiegeTower {
  towerId: TowerId;
  level: number;
  targeting: TargetingMode;
}

export interface SiegeBase {
  uid: string;
  displayName: string;
  flux: number;
  /** Slot index -> tower, or null for an empty pad. */
  towers: (SiegeTower | null)[];
  /** The wave the base is currently fighting. */
  wave: number;
  /** Highest wave cleared this run. Decides the Overclock payout. */
  runBestWave: number;
  /** Highest wave cleared across every run. Never resets. */
  bestWave: number;
  sigils: number;
  overclockCount: number;
  perks: SiegePerkId[];
  /**
   * Standing orders: launch each wave without waiting to be told.
   *
   * OFF by default, and that default is the point - a wave should not start
   * until you have looked at what is coming and pressed Launch. Turning it on
   * is also what lets the base fight while the tab is closed, because there is
   * nobody there to press anything; see settleSiege.
   */
  autoLaunch: boolean;
  lifetimeFlux: number;
  lifetimeWaves: number;
  /** The instant flux and wave are accurate as of. */
  settledAtMillis: number;
  createdAtMillis: number;
}

/**
 * The share of a wave you are allowed to let through before the base breaks.
 *
 * Integrity is measured against the wave in front of you rather than being a
 * flat pool, and that is deliberate. A flat pool that resets every wave means a
 * base with no towers at all survives its first twenty waves by simply standing
 * there while everything walks past - it never runs out of health, so it never
 * fails, so it "clears" waves it never fought. Pricing integrity as a fraction
 * of the wave makes doing nothing lose immediately at every wave number.
 */
/**
 * Flux every run opens with.
 *
 * This is a floor, not a gift: a board with no towers earns nothing, and a base
 * with no flux cannot buy a tower. Without enough here to afford the cheapest
 * gun, an Overclocked base would be permanently stuck at wave 1 with no way
 * back - which is exactly what the first version of Overclock did.
 */
export const SIEGE_STARTING_FLUX = 60;

export const LEAK_TOLERANCE = 0.34;
/** Even the smallest wave gets a couple of forgiven leaks. */
export const MIN_WAVE_INTEGRITY = 3;
export const AUTOSAVE_MILLIS = 25 * 1000;
/** Gaps this short are the page ticking, not an absence. Paid in full. */
export const ONLINE_GRACE_MILLIS = 90 * 1000;

const safeNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

export function createSiegeBase(uid: string, displayName: string, nowMillis: number): SiegeBase {
  return {
    uid,
    displayName: displayName || 'Operator',
    flux: SIEGE_STARTING_FLUX,
    towers: new Array(MAX_SIEGE_SLOTS).fill(null),
    wave: 1,
    runBestWave: 0,
    bestWave: 0,
    sigils: 0,
    overclockCount: 0,
    perks: [],
    autoLaunch: false,
    lifetimeFlux: 0,
    lifetimeWaves: 0,
    settledAtMillis: nowMillis,
    createdAtMillis: nowMillis,
  };
}

/** Repair a base read back from Firestore so a partial doc cannot poison the maths. */
export function normalizeSiegeBase(
  raw: Partial<SiegeBase> | undefined,
  uid: string,
  displayName: string,
  nowMillis: number,
): SiegeBase {
  const fresh = createSiegeBase(uid, displayName, nowMillis);
  if (!raw) return fresh;

  const towers: (SiegeTower | null)[] = new Array(MAX_SIEGE_SLOTS).fill(null);
  const rawTowers = Array.isArray(raw.towers) ? raw.towers : [];
  for (let i = 0; i < MAX_SIEGE_SLOTS; i++) {
    const entry = rawTowers[i];
    if (!entry || typeof entry !== 'object') continue;
    const towerId = (entry as SiegeTower).towerId;
    if (!TOWER_DEFS[towerId]) continue;
    const level = Math.min(TOWER_MAX_LEVEL, Math.max(1, Math.floor(safeNumber((entry as SiegeTower).level, 1))));
    const targeting = (entry as SiegeTower).targeting;
    towers[i] = {
      towerId,
      level,
      // Saves written before targeting existed have no mode at all.
      targeting: TARGETING_MODES.some((mode) => mode.id === targeting) ? targeting : 'first',
    };
  }

  const perks = Array.isArray(raw.perks)
    ? Array.from(new Set(raw.perks.filter((id): id is SiegePerkId => SIEGE_PERKS_BY_ID.has(id as SiegePerkId))))
    : [];

  return {
    ...fresh,
    ...raw,
    uid,
    displayName: raw.displayName || displayName || fresh.displayName,
    flux: Math.max(0, safeNumber(raw.flux, fresh.flux)),
    towers,
    wave: Math.max(1, Math.floor(safeNumber(raw.wave, 1))),
    runBestWave: Math.max(0, Math.floor(safeNumber(raw.runBestWave))),
    bestWave: Math.max(0, Math.floor(safeNumber(raw.bestWave))),
    sigils: Math.max(0, Math.floor(safeNumber(raw.sigils))),
    overclockCount: Math.max(0, Math.floor(safeNumber(raw.overclockCount))),
    perks,
    autoLaunch: raw.autoLaunch === true,
    lifetimeFlux: Math.max(0, safeNumber(raw.lifetimeFlux)),
    lifetimeWaves: Math.max(0, Math.floor(safeNumber(raw.lifetimeWaves))),
    settledAtMillis: safeNumber(raw.settledAtMillis, nowMillis),
    createdAtMillis: safeNumber(raw.createdAtMillis, nowMillis),
  };
}

// --- Derived stats -----------------------------------------------------------

export interface TowerStats {
  damage: number;
  fireRate: number;
  range: number;
  splash: number;
  splashFalloff: number;
  slow: number;
  slowSeconds: number;
  pierce: number;
  fluxPerSecond: number;
  dps: number;
}

/** Multipliers a base applies to every tower on it. */
export interface SiegeMultipliers {
  damage: number;
  fireRate: number;
  flux: number;
  integrity: number;
  speed: number;
}

export function getSiegeMultipliers(base: SiegeBase): SiegeMultipliers {
  const effects = getPerkEffects(base.perks);
  const sigils = Math.max(0, Math.floor(base.sigils || 0));
  // Every sector cleared this run leaves the base permanently stronger for the
  // rest of it. That is what pays for the board being stripped at a sector
  // boundary, and it is why a run reaches further than the last one even before
  // any sigils are involved.
  const sectors = getSectorsCleared(base);
  return {
    damage: effects.damageMultiplier * (1 + SIGIL_DAMAGE_BONUS * sigils) * (1 + SECTOR_DAMAGE_BONUS * sectors),
    fireRate: effects.fireRateMultiplier,
    flux: effects.fluxMultiplier * (1 + SIGIL_FLUX_BONUS * sigils) * (1 + SECTOR_FLUX_BONUS * sectors),
    integrity: effects.integrityMultiplier,
    speed: effects.speedMultiplier,
  };
}

export function getTowerStats(towerId: TowerId, level: number, multipliers: SiegeMultipliers): TowerStats {
  const def = TOWER_DEFS[towerId];
  const lvl = Math.max(1, Math.floor(level || 1));
  const scale = Math.pow(TOWER_DAMAGE_GROWTH, lvl - 1);
  const damage = def.damage * scale * multipliers.damage;
  const fireRate = Math.min(
    TOWER_FIRE_RATE_CAP,
    def.fireRate * Math.pow(TOWER_FIRE_RATE_GROWTH, lvl - 1) * multipliers.fireRate,
  );
  const pierce = def.pierce + Math.floor((lvl - 1) / 25);
  return {
    damage,
    fireRate,
    range: def.range,
    splash: def.splash,
    splashFalloff: def.splashFalloff,
    // Slow deepens with level but can never fully stop an orb; a 100% slow
    // would let one Frost Node hold a wave on the track forever.
    slow: Math.min(0.75, def.slow * (1 + (lvl - 1) * 0.012)),
    slowSeconds: def.slowSeconds,
    pierce,
    fluxPerSecond: def.fluxPerSecond * scale * multipliers.flux,
    dps: damage * fireRate * (1 + pierce),
  };
}

/** Everything a wave can leak at you, children included. */
export function getWaveLeakWeight(wave: number): number {
  return getWavePlan(wave).shells.reduce((sum, shell) => sum + getOrbTotalLeak(shell.tier), 0);
}

/** Integrity the base fields against `wave`. See LEAK_TOLERANCE. */
export function getWaveIntegrity(base: SiegeBase, wave: number): number {
  const budget = getWaveLeakWeight(wave) * LEAK_TOLERANCE * getSiegeMultipliers(base).integrity;
  return Math.max(MIN_WAVE_INTEGRITY, Math.round(budget));
}

export interface BaseSummary {
  /** Sum of every tower damage per second, pierce included. */
  dps: number;
  /** Flux per second from harvesters alone. */
  harvestPerSecond: number;
  towerCount: number;
  multipliers: SiegeMultipliers;
}

export function getBaseSummary(base: SiegeBase): BaseSummary {
  const multipliers = getSiegeMultipliers(base);
  let dps = 0;
  let harvest = 0;
  let towerCount = 0;
  for (const tower of base.towers) {
    if (!tower) continue;
    towerCount++;
    const stats = getTowerStats(tower.towerId, tower.level, multipliers);
    dps += stats.dps;
    harvest += stats.fluxPerSecond;
  }
  return { dps, harvestPerSecond: harvest, towerCount, multipliers };
}

// --- Buying ------------------------------------------------------------------

export interface PurchaseResult {
  base: SiegeBase;
  ok: boolean;
  /** Flux actually spent. Negative on a sale, zero on a rejected purchase. */
  spent: number;
  reason?: 'no-flux' | 'occupied' | 'empty' | 'bad-slot' | 'max-level';
}

const reject = (base: SiegeBase, reason: PurchaseResult['reason']): PurchaseResult => ({ base, ok: false, spent: 0, reason });

export function buildTower(base: SiegeBase, slot: number, towerId: TowerId): PurchaseResult {
  if (slot < 0 || slot >= MAX_SIEGE_SLOTS) return reject(base, 'bad-slot');
  if (!TOWER_DEFS[towerId]) return reject(base, 'bad-slot');
  if (base.towers[slot]) return reject(base, 'occupied');
  const cost = getTowerUpgradeCost(towerId, 0);
  if (base.flux < cost) return reject(base, 'no-flux');
  const towers = base.towers.slice();
  towers[slot] = { towerId, level: 1, targeting: 'first' };
  return { base: { ...base, towers, flux: base.flux - cost }, ok: true, spent: cost };
}

/** Point a tower at a different kind of target. Free, and changeable mid-wave. */
export function setTowerTargeting(base: SiegeBase, slot: number, targeting: TargetingMode): SiegeBase {
  if (slot < 0 || slot >= MAX_SIEGE_SLOTS) return base;
  const tower = base.towers[slot];
  if (!tower) return base;
  if (!TARGETING_MODES.some((mode) => mode.id === targeting)) return base;
  const towers = base.towers.slice();
  towers[slot] = { ...tower, targeting };
  return { ...base, towers };
}

export function upgradeTower(base: SiegeBase, slot: number): PurchaseResult {
  if (slot < 0 || slot >= MAX_SIEGE_SLOTS) return reject(base, 'bad-slot');
  const tower = base.towers[slot];
  if (!tower) return reject(base, 'empty');
  if (tower.level >= TOWER_MAX_LEVEL) return reject(base, 'max-level');
  const cost = getTowerUpgradeCost(tower.towerId, tower.level);
  if (base.flux < cost) return reject(base, 'no-flux');
  const towers = base.towers.slice();
  towers[slot] = { ...tower, level: tower.level + 1 };
  return { base: { ...base, towers, flux: base.flux - cost }, ok: true, spent: cost };
}

/** Refund for scrapping a tower: half of everything sunk into it. */
export const SELL_REFUND_RATE = 0.5;

export function sellTower(base: SiegeBase, slot: number): PurchaseResult {
  if (slot < 0 || slot >= MAX_SIEGE_SLOTS) return reject(base, 'bad-slot');
  const tower = base.towers[slot];
  if (!tower) return reject(base, 'empty');
  let invested = 0;
  for (let level = 0; level < tower.level; level++) invested += getTowerUpgradeCost(tower.towerId, level);
  const refund = Math.floor(invested * SELL_REFUND_RATE);
  const towers = base.towers.slice();
  towers[slot] = null;
  return { base: { ...base, towers, flux: base.flux + refund }, ok: true, spent: -refund };
}

/**
 * The Auto-Deploy Core shopping list, and the only spending model used for
 * offline progress.
 *
 * It buys the cheapest useful thing: fill an empty pad if one is affordable and
 * the board is thin, otherwise level up the cheapest tower. Deliberately dumb -
 * a player who plans placements should always beat it, or paying for it would
 * be paying to stop playing.
 */
export function planAutoDeploy(base: SiegeBase): { slot: number; towerId?: TowerId; cost: number } | null {
  const emptySlots: number[] = [];
  base.towers.forEach((tower, index) => { if (!tower) emptySlots.push(index); });

  const towerCount = MAX_SIEGE_SLOTS - emptySlots.length;
  if (emptySlots.length > 0) {
    // A rough ladder: a gun first so the board can shoot at all, then economy,
    // then the heavier tiers as they come into budget.
    const wish: TowerId[] = towerCount === 0
      ? ['pulse']
      : towerCount < 2
        ? ['harvest', 'pulse']
        : towerCount < 4
          ? ['scatter', 'harvest', 'pulse']
          : towerCount < 6
            ? ['frost', 'scatter', 'pulse']
            : ['railgun', 'lance', 'scatter', 'pulse'];
    for (const towerId of wish) {
      const cost = getTowerUpgradeCost(towerId, 0);
      if (base.flux >= cost) return { slot: emptySlots[0], towerId, cost };
    }
  }

  let bestSlot = -1;
  let bestCost = Number.POSITIVE_INFINITY;
  base.towers.forEach((tower, index) => {
    if (!tower || tower.level >= TOWER_MAX_LEVEL) return;
    const cost = getTowerUpgradeCost(tower.towerId, tower.level);
    if (cost < bestCost) { bestCost = cost; bestSlot = index; }
  });
  if (bestSlot >= 0 && base.flux >= bestCost) return { slot: bestSlot, cost: bestCost };
  return null;
}

/**
 * How many purchases one auto-deploy pass may make.
 *
 * This is a runtime bound, not a budget: the planner stops on its own the
 * moment nothing is affordable. It has to be generous enough to rebuild a whole
 * board in one pass, because a sector clear strips every tower - an earlier cap
 * of 40 left auto-deploy bases permanently stuck at the first sector boundary,
 * unable to re-arm fast enough to clear the wave that would pay for re-arming.
 */
export const AUTO_DEPLOY_MAX_PURCHASES = 2500;

export function runAutoDeploy(base: SiegeBase, maxPurchases = AUTO_DEPLOY_MAX_PURCHASES): { base: SiegeBase; purchases: number } {
  let current = base;
  let purchases = 0;
  for (let i = 0; i < maxPurchases; i++) {
    const plan = planAutoDeploy(current);
    if (!plan) break;
    const result = plan.towerId
      ? buildTower(current, plan.slot, plan.towerId)
      : upgradeTower(current, plan.slot);
    if (!result.ok) break;
    current = result.base;
    purchases++;
  }
  return { base: current, purchases };
}

// --- Sector clear ------------------------------------------------------------

export interface SectorClear {
  base: SiegeBase;
  /** The sector just finished, and the board that comes next. */
  sector: number;
  nextMap: SiegeMap;
  /** Flux handed back for the board that was dismantled. */
  refund: number;
  towersRecovered: number;
}

/**
 * Finish a sector: the board is dismantled and the siege moves to a new map.
 *
 * The reset is the reward, not a punishment. Everything sunk into the old board
 * comes back as flux at FULL value (not the half a manual scrap pays), the
 * sector bonus makes the next twenty waves easier than the last twenty, and the
 * new map is a genuinely different shape to solve. Refunding in full matters:
 * the point is to make you re-plan, not to tax you for having played well.
 */
export function clearSector(base: SiegeBase): SectorClear {
  let refund = 0;
  let towersRecovered = 0;
  for (const tower of base.towers) {
    if (!tower) continue;
    towersRecovered++;
    for (let level = 0; level < tower.level; level++) refund += getTowerUpgradeCost(tower.towerId, level);
  }
  refund = Math.floor(refund);

  return {
    base: {
      ...base,
      flux: base.flux + refund,
      towers: new Array(MAX_SIEGE_SLOTS).fill(null),
    },
    sector: getSector(base.wave) - 1,
    nextMap: getMapForWave(base.wave),
    refund,
    towersRecovered,
  };
}

// --- Perk purchase -----------------------------------------------------------

export interface PerkPurchase {
  base: SiegeBase;
  ok: boolean;
  perk?: SiegePerk;
  reason?: 'unknown' | 'owned' | 'funds';
}

/**
 * Spending real credits or nano cores happens on the users doc in the client;
 * this only decides whether it is allowed and records the perk on the base.
 */
export function buySiegePerk(
  base: SiegeBase,
  perkId: SiegePerkId,
  wallet: { credits: number; nanoCores: number },
): PerkPurchase {
  const perk = SIEGE_PERKS_BY_ID.get(perkId);
  if (!perk) return { base, ok: false, reason: 'unknown' };
  if ((base.perks || []).includes(perkId)) return { base, ok: false, reason: 'owned' };
  const balance = perk.currency === 'credits' ? wallet.credits : wallet.nanoCores;
  if (!(balance >= perk.price)) return { base, ok: false, reason: 'funds' };
  return { base: { ...base, perks: [...(base.perks || []), perkId] }, ok: true, perk };
}

// --- Overclock ---------------------------------------------------------------

export function overclock(base: SiegeBase, nowMillis: number): SiegeBase {
  const earned = getOverclockSigils(base);
  if (earned <= 0) return base;
  const effects = getPerkEffects(base.perks);
  return {
    ...base,
    // Never below the opening float - a stripped board with no flux cannot buy
    // its first gun, and a base that cannot shoot can never earn again.
    flux: Math.max(SIEGE_STARTING_FLUX, effects.startingFlux),
    towers: new Array(MAX_SIEGE_SLOTS).fill(null),
    wave: 1,
    runBestWave: 0,
    sigils: base.sigils + earned,
    overclockCount: (base.overclockCount || 0) + 1,
    settledAtMillis: nowMillis,
  };
}

// --- Live simulation ---------------------------------------------------------

export interface SiegeOrb {
  id: number;
  tier: OrbTierId;
  hp: number;
  maxHp: number;
  /** Board units walked along the track. */
  distance: number;
  /** Seconds of slow left, and how strong it is. */
  slowSeconds: number;
  slowFactor: number;
  hpScale: number;
  traits: OrbTrait[];
  /** Flat damage soaked from every hit. Zero unless armoured. */
  armor: number;
  /** Seconds since anything hit this shell, for regeneration. */
  sinceDamaged: number;
}

export interface SiegeShot {
  slot: number;
  from: SiegePoint;
  to: SiegePoint;
  color: string;
  splash: number;
  /** Seconds of life left, for the renderer. */
  life: number;
}

/**
 * `building` is the phase this game is designed around: the wave is planned and
 * visible, nothing is moving, and it stays that way until the player launches
 * it. Every other status is a consequence of that launch.
 */
export type SiegeRunStatus = 'building' | 'running' | 'cleared' | 'breached';

/** How long Overdrive runs, and how long before it can be used again. */
export const OVERDRIVE_SECONDS = 6;
export const OVERDRIVE_FIRE_RATE = 2.2;

export interface SiegeRun {
  wave: number;
  status: SiegeRunStatus;
  /** The board this wave is fought on. Changes when a sector does. */
  mapId: string;
  /** The plan for this wave, readable during the build phase. */
  plan: WavePlan;
  /** Seconds of Overdrive left, and whether it has been spent this wave. */
  overdriveSecondsLeft: number;
  overdriveUsed: boolean;
  orbs: SiegeOrb[];
  /** Shells still queued to walk on, heaviest first. */
  pending: WaveShell[];
  spawnTimer: number;
  spawnIntervalSeconds: number;
  integrity: number;
  maxIntegrity: number;
  /** Flux earned inside this wave from popped shells. */
  bounty: number;
  elapsedSeconds: number;
  cooldowns: number[];
  shots: SiegeShot[];
  nextOrbId: number;
  /** Popped this wave, for the HUD. */
  popped: number;
  leaked: number;
}

/**
 * Prepare `wave`. Nothing moves until `launchWave` is called.
 *
 * The build phase is the whole reason the game reads as tower defence rather
 * than a screensaver: the wave's composition and modifier are on the table, and
 * you get unlimited time to answer them before anything walks.
 */
export function startWave(base: SiegeBase, wave: number): SiegeRun {
  const plan = getWavePlan(wave);
  const maxIntegrity = getWaveIntegrity(base, wave);
  return {
    wave: plan.wave,
    status: 'building',
    mapId: getMapForWave(wave).id,
    plan,
    overdriveSecondsLeft: 0,
    overdriveUsed: false,
    orbs: [],
    // Heaviest first, so the wave opens with its threat rather than ending on it.
    pending: plan.shells.slice(),
    spawnTimer: 0,
    spawnIntervalSeconds: plan.spawnIntervalSeconds,
    integrity: maxIntegrity,
    maxIntegrity,
    bounty: 0,
    elapsedSeconds: 0,
    cooldowns: new Array(MAX_SIEGE_SLOTS).fill(0),
    shots: [],
    nextOrbId: 1,
    popped: 0,
    leaked: 0,
  };
}

/** Send the prepared wave in. A no-op unless the run is still building. */
export function launchWave(run: SiegeRun): SiegeRun {
  if (run.status !== 'building') return run;
  return { ...run, status: 'running' };
}

/**
 * Overdrive: one burst of fire rate per wave, on demand.
 *
 * The one thing the player can do with their hands once a wave is walking. It
 * is deliberately once per wave rather than on a timer, so spending it is a
 * decision about THIS wave rather than a rotation to remember.
 */
export function activateOverdrive(run: SiegeRun): SiegeRun {
  if (run.status !== 'running' || run.overdriveUsed) return run;
  return { ...run, overdriveUsed: true, overdriveSecondsLeft: OVERDRIVE_SECONDS };
}

export function isOverdriveActive(run: SiegeRun): boolean {
  return run.overdriveSecondsLeft > 0;
}

/** Flux paid for peeling one shell. Small; the wave clear is the real payday. */
export function getOrbBounty(tier: OrbTierId): number {
  return ORB_TIERS[tier].shellHp * 0.4;
}

/** Build one shell, armour and traits included. */
function makeOrb(id: number, shell: WaveShell, distance: number): SiegeOrb {
  const tier = ORB_TIERS[shell.tier];
  const hp = tier.shellHp * shell.hpScale;
  return {
    id,
    tier: shell.tier,
    hp,
    maxHp: hp,
    distance,
    slowSeconds: 0,
    slowFactor: 0,
    hpScale: shell.hpScale,
    traits: shell.traits,
    // Armour scales with the shell it is bolted to, so it stays a meaningful
    // share of a hit at wave 200 as well as at wave 20.
    armor: shell.traits.includes('armored') ? hp * ARMOR_SHARE : 0,
    sinceDamaged: 0,
  };
}

const MAX_STEP_SECONDS = 0.05;
const MAX_ORBS = 260;

/**
 * What a hit of `amount` actually lands through `armor`.
 *
 * Flat subtraction rather than a percentage, and that is the whole design: a
 * percentage would scale every gun equally and change nothing about which
 * towers you want. Taking a fixed bite out of each hit makes a wall of cheap
 * fast shots nearly worthless and leaves one heavy shot barely touched, which
 * is what forces an armoured wave to be answered with different towers.
 */
export function getArmoredDamage(amount: number, armor: number): number {
  if (!(armor > 0)) return amount;
  return Math.max(amount * (1 - ARMOR_MAX_REDUCTION), amount - armor);
}

function damageOrb(run: SiegeRun, orb: SiegeOrb, amount: number, multipliers: SiegeMultipliers, isSplash = false): void {
  // Shielded shells ignore collateral entirely - splash still lands on their
  // neighbours, it just does not carry to them.
  if (isSplash && orb.traits.includes('shielded')) return;

  const dealt = getArmoredDamage(amount, orb.armor);
  orb.hp -= dealt;
  orb.sinceDamaged = 0;
  if (orb.hp > 0) return;
  const tier = ORB_TIERS[orb.tier];
  run.popped++;
  run.bounty += getOrbBounty(orb.tier) * multipliers.flux;
  if (tier.child && run.orbs.length + tier.childCount <= MAX_ORBS) {
    for (let i = 0; i < tier.childCount; i++) {
      const child = makeOrb(
        run.nextOrbId++,
        { tier: tier.child, hpScale: orb.hpScale, traits: orb.traits },
        // Children spill out behind the parent so they do not all overlap.
        Math.max(0, orb.distance - i * 1.1),
      );
      child.slowSeconds = orb.slowSeconds;
      child.slowFactor = orb.slowFactor;
      run.orbs.push(child);
    }
  } else if (tier.child) {
    // The board is saturated. Rather than silently deleting health (which would
    // hand the player a free wave), pay the children bounty and count them as
    // popped - the wave still costs the same flux either way.
    run.bounty += tier.childCount * getOrbBounty(tier.child) * multipliers.flux;
  }
  orb.hp = 0;
}

/**
 * Advance the siege by `dtSeconds`.
 *
 * Returns a fresh run object; the base is never mutated. Called every animation
 * frame by the board and in fixed chunks by the catch-up fast-forward, so it
 * has to stay deterministic: targeting is always "the orb furthest along the
 * track", never a random pick.
 */
export function stepSiege(run: SiegeRun, base: SiegeBase, dtSeconds: number): SiegeRun {
  if (run.status !== 'running') return run;
  const map = getSiegeMap(run.mapId);
  const multipliers = getSiegeMultipliers(base);
  let remaining = Math.max(0, Math.min(dtSeconds, 5)) * multipliers.speed;

  const next: SiegeRun = {
    ...run,
    orbs: run.orbs.map((orb) => ({ ...orb })),
    pending: run.pending.slice(),
    cooldowns: run.cooldowns.slice(),
    shots: run.shots.map((shot) => ({ ...shot })),
  };

  while (remaining > 0 && next.status === 'running') {
    const dt = Math.min(remaining, MAX_STEP_SECONDS);
    remaining -= dt;
    next.elapsedSeconds += dt;

    // 1. Spawning.
    if (next.pending.length > 0) {
      next.spawnTimer -= dt;
      while (next.spawnTimer <= 0 && next.pending.length > 0) {
        const shell = next.pending.shift()!;
        next.orbs.push(makeOrb(next.nextOrbId++, shell, 0));
        next.spawnTimer += next.spawnIntervalSeconds;
      }
    }

    // 2. Movement, regeneration and leaks.
    for (const orb of next.orbs) {
      if (orb.hp <= 0) continue;
      const slow = orb.slowSeconds > 0 ? orb.slowFactor : 0;
      const swift = orb.traits.includes('swift') ? SWIFT_SPEED : 1;
      orb.distance += ORB_TIERS[orb.tier].speed * swift * (1 - slow) * dt;
      if (orb.slowSeconds > 0) orb.slowSeconds = Math.max(0, orb.slowSeconds - dt);

      orb.sinceDamaged += dt;
      if (orb.traits.includes('regen') && orb.sinceDamaged >= REGEN_DELAY_SECONDS && orb.hp < orb.maxHp) {
        orb.hp = Math.min(orb.maxHp, orb.hp + orb.maxHp * REGEN_PER_SECOND * dt);
      }

      if (orb.distance >= map.length) {
        // A shell that gets through carries everything still inside it, so it
        // costs the whole nested leak value, not just its own outer layer.
        next.integrity -= getOrbTotalLeak(orb.tier);
        next.leaked++;
        orb.hp = 0;
      }
    }

    // 3. Firing. Each tower picks its target according to its own targeting
    //    mode, which is what makes where a tower stands mean something.
    if (next.overdriveSecondsLeft > 0) next.overdriveSecondsLeft = Math.max(0, next.overdriveSecondsLeft - dt);
    const overdrive = next.overdriveSecondsLeft > 0 ? OVERDRIVE_FIRE_RATE : 1;

    const live = next.orbs.filter((orb) => orb.hp > 0);
    if (live.length > 0) {
      // Default order is "furthest along the track", which is `first`.
      live.sort((a, b) => b.distance - a.distance);
      for (let slot = 0; slot < map.slots.length; slot++) {
        const tower = base.towers[slot];
        if (!tower) continue;
        const stats = getTowerStats(tower.towerId, tower.level, multipliers);
        if (stats.fireRate <= 0 || stats.damage <= 0) continue;
        next.cooldowns[slot] -= dt;
        if (next.cooldowns[slot] > 0) continue;

        const origin = map.slots[slot];
        const inRange = live.filter((orb) => {
          if (orb.hp <= 0) return false;
          const at = pointOnMap(map, orb.distance);
          return Math.hypot(at.x - origin.x, at.y - origin.y) <= stats.range;
        });
        if (inRange.length === 0) continue;

        if (tower.targeting === 'last') {
          inRange.reverse();
        } else if (tower.targeting === 'strongest') {
          inRange.sort((a, b) => b.hp - a.hp);
        } else if (tower.targeting === 'closest') {
          inRange.sort((a, b) => {
            const pa = pointOnMap(map, a.distance);
            const pb = pointOnMap(map, b.distance);
            return Math.hypot(pa.x - origin.x, pa.y - origin.y) - Math.hypot(pb.x - origin.x, pb.y - origin.y);
          });
        }

        let hits = 0;
        let firstHit: SiegeOrb | null = null;
        for (const orb of inRange) {
          if (orb.hp <= 0) continue;
          if (!firstHit) firstHit = orb;
          damageOrb(next, orb, stats.damage, multipliers);
          if (stats.slow > 0) {
            orb.slowSeconds = Math.max(orb.slowSeconds, stats.slowSeconds);
            orb.slowFactor = Math.max(orb.slowFactor, stats.slow);
          }
          hits++;
          if (hits > stats.pierce) break;
        }

        if (!firstHit) continue;

        if (stats.splash > 0) {
          const centre = pointOnMap(map, firstHit.distance);
          for (const orb of next.orbs) {
            if (orb.hp <= 0 || orb.id === firstHit.id) continue;
            const at = pointOnMap(map, orb.distance);
            if (Math.hypot(at.x - centre.x, at.y - centre.y) > stats.splash) continue;
            damageOrb(next, orb, stats.damage * stats.splashFalloff, multipliers, true);
            if (stats.slow > 0) {
              orb.slowSeconds = Math.max(orb.slowSeconds, stats.slowSeconds);
              orb.slowFactor = Math.max(orb.slowFactor, stats.slow);
            }
          }
        }

        next.cooldowns[slot] = 1 / (stats.fireRate * overdrive);
        next.shots.push({
          slot,
          from: origin,
          to: pointOnMap(map, firstHit.distance),
          color: TOWER_DEFS[tower.towerId].color,
          splash: stats.splash,
          life: 0.12,
        });
      }
    }

    // 4. Cleanup and end conditions.
    next.orbs = next.orbs.filter((orb) => orb.hp > 0);
    next.shots = next.shots
      .map((shot) => ({ ...shot, life: shot.life - dt }))
      .filter((shot) => shot.life > 0);

    if (next.integrity <= 0) {
      next.integrity = 0;
      next.status = 'breached';
    } else if (next.orbs.length === 0 && next.pending.length === 0) {
      next.status = 'cleared';
    }
  }

  return next;
}

/** Flux a cleared wave hands over: the clear reward plus everything popped. */
export function getWavePayout(base: SiegeBase, run: SiegeRun): number {
  const multipliers = getSiegeMultipliers(base);
  return getWaveReward(run.wave) * multipliers.flux + run.bounty;
}

// --- Offline settlement ------------------------------------------------------

export interface OfflineReport {
  awayMillis: number;
  /** Time actually paid for, after the cap. */
  creditedMillis: number;
  wasCapped: boolean;
  fluxEarned: number;
  wavesCleared: number;
  fromWave: number;
  toWave: number;
  autoPurchases: number;
  /** True when the base met a wave it could not clear and sat there. */
  stalled: boolean;
  /** Sector boundaries crossed while away, each one a new map. */
  sectorsCleared: number;
  /** True when nothing was launched because standing orders are off. */
  heldForLaunch: boolean;
}

/**
 * The share of the track at least one armed tower can shoot into.
 *
 * Sampled rather than integrated because the track is a polyline and the towers
 * are circles; 240 samples is finer than the eye can see on the board and costs
 * nothing.
 */
export function getTrackCoverage(base: SiegeBase, map: SiegeMap = DEFAULT_SIEGE_MAP): number {
  const multipliers = getSiegeMultipliers(base);
  const armed: { point: SiegePoint; range: number }[] = [];
  base.towers.forEach((tower, slot) => {
    if (!tower) return;
    // A tower parked on a pad this map does not have cannot shoot at anything.
    const point = map.slots[slot];
    if (!point) return;
    const stats = getTowerStats(tower.towerId, tower.level, multipliers);
    if (stats.damage <= 0 || stats.fireRate <= 0) return;
    armed.push({ point, range: stats.range });
  });
  if (armed.length === 0) return 0;

  const SAMPLES = 240;
  let covered = 0;
  for (let i = 0; i < SAMPLES; i++) {
    const at = pointOnMap(map, (map.length * i) / SAMPLES);
    if (armed.some((tower) => Math.hypot(at.x - tower.point.x, at.y - tower.point.y) <= tower.range)) covered++;
  }
  return covered / SAMPLES;
}

/**
 * How long this base needs to clear `wave`, or null if it cannot.
 *
 * A closed-form estimate rather than a replay: wave health over damage per
 * second, discounted by the share of the track the guns actually cover. It is
 * deliberately a little pessimistic (it ignores splash overkill), which keeps
 * offline progress from ever outpacing sitting and watching.
 */
export function estimateWaveSeconds(base: SiegeBase, wave: number): number | null {
  const summary = getBaseSummary(base);
  if (summary.dps <= 0) return null;
  const map = getMapForWave(wave);
  const coverage = getTrackCoverage(base, map);
  if (coverage <= 0) return null;

  const plan = getWavePlan(wave);
  const multipliers = getSiegeMultipliers(base);
  const leadTier = plan.shells[0]?.tier || 'spark';
  const trait = plan.modifier.trait;

  // Modifiers change the sum, so offline progress has to feel them too - or a
  // base would sail through waves offline that it visibly cannot beat on screen.
  const swift = trait === 'swift' ? SWIFT_SPEED : 1;
  const walkSeconds = map.length / (ORB_TIERS[leadTier].speed * swift);

  // Armour is a flat bite out of every hit, so its cost depends on how hard the
  // board hits: many small shots lose far more of their damage than few big
  // ones. Approximated from the average damage of the armed towers.
  let armorPenalty = 1;
  if (trait === 'armored') {
    const armed = base.towers.filter((tower) => tower && TOWER_DEFS[tower.towerId].damage > 0) as SiegeTower[];
    const averageHit = armed.length > 0
      ? armed.reduce((sum, tower) => sum + getTowerStats(tower.towerId, tower.level, multipliers).damage, 0) / armed.length
      : 0;
    const averageArmor = (plan.totalHp / Math.max(1, plan.shells.length)) * ARMOR_SHARE;
    armorPenalty = averageHit > 0
      ? Math.max(1 - ARMOR_MAX_REDUCTION, (averageHit - averageArmor) / averageHit)
      : 1 - ARMOR_MAX_REDUCTION;
  }

  // Regeneration is a drain the board has to out-damage - but only shells that
  // are NOT currently being shot at heal, so a board that covers more of the
  // track suffers proportionally less of it. Modelling it as always-on made
  // coverage worthless against the one modifier coverage is the answer to.
  const regenDrain = trait === 'regen' ? plan.totalHp * REGEN_PER_SECOND * (1 - coverage) : 0;

  const effectiveDps = Math.max(0, summary.dps * coverage * armorPenalty - regenDrain);
  if (effectiveDps <= 0) return null;

  // If the column outruns the guns some of it leaks, and enough leaks end the
  // wave. This is the check that decides a base has hit its wall.
  const leakFraction = Math.max(0, 1 - (walkSeconds * effectiveDps) / plan.totalHp);
  const leakDamage = getWaveLeakWeight(wave) * leakFraction;
  if (leakDamage >= getWaveIntegrity(base, wave)) return null;

  const killSeconds = plan.totalHp / effectiveDps;
  const spawnSeconds = plan.shells.length * plan.spawnIntervalSeconds;
  return Math.max(1, (killSeconds + spawnSeconds * 0.5) / multipliers.speed) + walkSeconds * 0.15;
}

/**
 * Bring a base up to `nowMillis`.
 *
 * Short gaps are the page ticking and pay in full. Long gaps are an absence:
 * capped, discounted, and reported back so the player can be told what they
 * missed. With Auto-Deploy the base also spends its takings between waves,
 * which is the difference between coming back to progress and coming back to a
 * pile of flux and the same wave.
 */
export function settleSiege(base: SiegeBase, nowMillis: number): { base: SiegeBase; offline: OfflineReport | null } {
  const gap = nowMillis - safeNumber(base.settledAtMillis, nowMillis);
  if (!(gap > 0)) {
    return { base: { ...base, settledAtMillis: Math.max(base.settledAtMillis, nowMillis) }, offline: null };
  }

  const effects = getPerkEffects(base.perks);
  const isAbsence = gap > ONLINE_GRACE_MILLIS;

  /**
   * Nothing launches itself unless it was told to.
   *
   * Waves wait for a player, and there is no player during an absence - so an
   * away base only fights if standing orders are on (the auto-launch toggle, or
   * the Auto-Deploy Core that includes it). Otherwise it still collects from its
   * harvesters and is sitting in the build phase when you get back, which is
   * exactly where you left it.
   */
  const standingOrders = base.autoLaunch === true || effects.autoDeploy;
  const creditedMillis = isAbsence ? Math.min(gap, effects.offlineCapMillis) : gap;
  const efficiency = isAbsence ? effects.offlineEfficiency : 1;

  let current: SiegeBase = { ...base };
  let secondsLeft = (creditedMillis / 1000) * efficiency;
  const startWaveNumber = current.wave;
  let waves = 0;
  let flux = 0;
  let purchases = 0;
  let stalled = false;
  let sectorsCleared = 0;

  // Harvesters print regardless of combat, so pay them for the whole window up
  // front and let the wave loop spend it.
  const harvested = getBaseSummary(current).harvestPerSecond * secondsLeft;
  flux += harvested;
  current = { ...current, flux: current.flux + harvested };

  // Cap the loop: a very strong base could otherwise clear thousands of waves
  // in one settle and lock the tab up doing it.
  const MAX_OFFLINE_WAVES = 600;
  while (standingOrders && secondsLeft > 0 && waves < MAX_OFFLINE_WAVES) {
    const seconds = estimateWaveSeconds(current, current.wave);
    if (seconds === null) { stalled = true; break; }
    if (seconds > secondsLeft) break;
    secondsLeft -= seconds;

    // Multipliers are re-read each wave because the sector bonus grows as the
    // run does; hoisting this out of the loop would freeze the base at its
    // first-wave strength for the whole absence.
    const multipliers = getSiegeMultipliers(current);
    const payout = getWaveReward(current.wave) * multipliers.flux;
    flux += payout;
    const clearedWave = current.wave;
    current = {
      ...current,
      flux: current.flux + payout,
      wave: clearedWave + 1,
      runBestWave: Math.max(current.runBestWave, clearedWave),
      bestWave: Math.max(current.bestWave, clearedWave),
      lifetimeWaves: current.lifetimeWaves + 1,
    };
    waves++;

    // A sector boundary strips the board and moves to a new map. Offline has to
    // do this too, or an away base would sit at wave 20 forever holding towers
    // that the next map has no pads for.
    if (isSectorFinalWave(clearedWave)) {
      const cleared = clearSector(current);
      current = cleared.base;
      sectorsCleared++;
    }

    if (effects.autoDeploy) {
      const deployed = runAutoDeploy(current);
      current = deployed.base;
      purchases += deployed.purchases;
    }
  }

  current = {
    ...current,
    lifetimeFlux: current.lifetimeFlux + flux,
    settledAtMillis: nowMillis,
  };

  if (!isAbsence) return { base: current, offline: null };

  return {
    base: current,
    offline: {
      awayMillis: gap,
      creditedMillis,
      wasCapped: gap > effects.offlineCapMillis,
      fluxEarned: flux,
      wavesCleared: waves,
      fromWave: startWaveNumber,
      toWave: current.wave,
      autoPurchases: purchases,
      sectorsCleared,
      stalled,
      heldForLaunch: !standingOrders,
    },
  };
}

// --- The lobby ladder --------------------------------------------------------

/**
 * What a room shares.
 *
 * Nano Siege in a lobby is not one shared board: everyone plays the base their
 * own account already owns, and the room is the scoreboard between them. So the
 * only thing on the lobby doc is how far each player has pushed - a few numbers
 * per person, written every few seconds rather than every frame.
 */
export interface NanoSiegeEntry {
  uid: string;
  displayName: string;
  wave: number;
  bestWave: number;
  sigils: number;
  updatedAtMillis: number;
}

export interface NanoSiegeState {
  state: 'playing' | 'finished';
  startedAtMillis: number;
  entries: Record<string, NanoSiegeEntry>;
  winnerId: string | null;
}

/** How often a client publishes its wave to the room. */
export const LADDER_PUBLISH_MILLIS = 8 * 1000;

export function createInitialNanoSiegeState(nowMillis = Date.now()): NanoSiegeState {
  return { state: 'playing', startedAtMillis: nowMillis, entries: {}, winnerId: null };
}

/**
 * Repair a lobby state read off the doc.
 *
 * Not optional politeness: a client writing only its own entry through a dotted
 * path (`nanoSiege.entries.<uid>`) creates the parent object WITHOUT the other
 * fields when the game has never been initialised. Anything that trusted
 * `lobby.nanoSiege.state` to exist would then crash the whole lobby, which is a
 * failure this codebase has already shipped once.
 */
export function normalizeNanoSiegeState(raw: Partial<NanoSiegeState> | undefined): NanoSiegeState {
  const base = createInitialNanoSiegeState(safeNumber(raw?.startedAtMillis, Date.now()));
  if (!raw) return base;

  const entries: Record<string, NanoSiegeEntry> = {};
  for (const [uid, entry] of Object.entries(raw.entries || {})) {
    if (!entry || typeof entry !== 'object') continue;
    entries[uid] = {
      uid: entry.uid || uid,
      displayName: entry.displayName || 'Operator',
      wave: Math.max(1, Math.floor(safeNumber(entry.wave, 1))),
      bestWave: Math.max(0, Math.floor(safeNumber(entry.bestWave))),
      sigils: Math.max(0, Math.floor(safeNumber(entry.sigils))),
      updatedAtMillis: safeNumber(entry.updatedAtMillis),
    };
  }

  return {
    state: raw.state === 'finished' ? 'finished' : 'playing',
    startedAtMillis: base.startedAtMillis,
    entries,
    winnerId: typeof raw.winnerId === 'string' ? raw.winnerId : null,
  };
}

/** The room ordered by how far each operator has pushed. */
export function getLadder(state: NanoSiegeState): NanoSiegeEntry[] {
  return Object.values(state.entries || {}).sort(
    (a, b) => b.bestWave - a.bestWave || b.wave - a.wave || b.sigils - a.sigils,
  );
}

/** Who wins if the host closes the siege now, or null if nobody has played. */
export function getLadderWinner(state: NanoSiegeState): string | null {
  const ladder = getLadder(state);
  return ladder.length > 0 ? ladder[0].uid : null;
}

// --- Formatting --------------------------------------------------------------

const SUFFIXES = ['', 'K', 'M', 'B', 'T', 'aa', 'ab', 'ac', 'ad', 'ae', 'af', 'ag', 'ah'];

/** Compact flux for the HUD. Idle numbers get very large very fast. */
export function formatFlux(value: number): string {
  const amount = Number.isFinite(value) ? value : 0;
  const sign = amount < 0 ? '-' : '';
  let n = Math.abs(amount);
  if (n < 1000) return `${sign}${Math.floor(n)}`;
  let tier = 0;
  while (n >= 1000 && tier < SUFFIXES.length - 1) { n /= 1000; tier++; }
  return `${sign}${n.toFixed(n < 10 ? 2 : n < 100 ? 1 : 0)}${SUFFIXES[tier]}`;
}

/** "3h 12m" - how long the base was left alone. */
export function formatAwayTime(millis: number): string {
  const totalMinutes = Math.max(0, Math.floor(millis / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${Math.max(1, minutes)}m`;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}
